import type {
  CheckoutAction,
  CommerceCheckoutConfigView,
  CommerceProductView,
  ComponentQuotaUsageView,
  UserCouponView,
  CreditAccountView,
  CreditLedgerEntryView,
  MembershipOverviewView,
  OrderQuoteView,
  OrderSourceType,
  OrderView,
  PaymentChannel,
  PublisherEarningsOverviewView,
  PublisherPayoutProfileView,
  PublisherSettlementView,
  PublisherWithdrawalView,
  RecurringPaymentChannel,
  SubscriptionView,
} from 'shared';
import { http } from './client';
import type {
  CreditLedgerQuery,
  CreditLedgerPage,
  CreditMonthView,
  CreditUsageSummary,
  OrderPage,
} from 'shared';

export const commerceApi = {
  marketplaceBilling: (packageId: string, releaseId?: string): Promise<import('shared').MarketplaceBillingView> =>
    http.get(`/commerce/marketplace/${encodeURIComponent(packageId)}/billing`, { params: { releaseId } }),
  marketplaceQuote: (packageId: string, input: { releaseId?: string; planId: string; renewal?: boolean }): Promise<import('shared').MarketplaceUpgradeQuoteView> =>
    http.post(`/commerce/marketplace/${encodeURIComponent(packageId)}/quote`, input),
  products: (): Promise<CommerceProductView[]> => http.get('/commerce/products'),
  membership: (): Promise<MembershipOverviewView> => http.get('/commerce/membership'),
  componentQuotaUsage: (workspaceId?: string): Promise<ComponentQuotaUsageView> =>
    http.get(
      `/commerce/quota-usage/components${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  deleteOtherQuotaUsage: (itemId: string, workspaceId?: string): Promise<void> =>
    http.delete(
      `/commerce/quota-usage/other/${encodeURIComponent(itemId)}${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  checkoutConfig: (): Promise<CommerceCheckoutConfigView> => http.get('/commerce/checkout-config'),
  orders: (cursor?: string): Promise<OrderPage> =>
    http.get('/commerce/orders', { params: { cursor, limit: 20 } }),
  publisherEarnings: (): Promise<PublisherEarningsOverviewView> =>
    http.get('/commerce/publisher/earnings'),
  publisherSettlements: (): Promise<PublisherSettlementView[]> =>
    http.get('/commerce/publisher/settlements'),
  payoutProfile: (): Promise<PublisherPayoutProfileView> =>
    http.get('/commerce/publisher/payout-profile'),
  startAlipayPayoutVerification: (input: {
    realName: string;
    idNumber: string;
    alipayAccount: string;
  }) =>
    http.post<{ redirectUrl: string }>(
      '/commerce/publisher/payout-profile/alipay-certification',
      input,
    ),
  syncAlipayPayoutVerification: () =>
    http.post<PublisherPayoutProfileView>(
      '/commerce/publisher/payout-profile/alipay-certification/sync',
    ),
  publisherWithdrawals: (): Promise<PublisherWithdrawalView[]> =>
    http.get('/commerce/publisher/withdrawals'),
  requestWithdrawal: (amountMinor: number, idempotencyKey: string = crypto.randomUUID()): Promise<PublisherWithdrawalView> =>
    http.post(
      '/commerce/publisher/withdrawals',
      { amountMinor },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
  cancelWithdrawal: (withdrawalId: string): Promise<PublisherWithdrawalView> =>
    http.post(`/commerce/publisher/withdrawals/${withdrawalId}/cancel`),
  subscriptions: (): Promise<SubscriptionView[]> => http.get('/commerce/subscriptions'),
  createSubscription: (
    productId: string,
    channel: RecurringPaymentChannel,
  ): Promise<{ subscription: SubscriptionView; action: CheckoutAction }> =>
    http.post('/commerce/subscriptions', { productId, channel }),
  cancelSubscription: (subscriptionId: string): Promise<void> =>
    http.post(`/commerce/subscriptions/${subscriptionId}/cancel`),
  order: (orderId: string): Promise<OrderView> => http.get(`/commerce/orders/${orderId}`),
  quoteOrder: (input: {
    sourceType: OrderSourceType;
    sourceId: string;
    quantity?: number;
    couponId?: string | null;
  }): Promise<OrderQuoteView> => http.post('/commerce/orders/quote', input),
  createOrder: (input: {
    billingQuoteId?: string;
    sourceType: OrderSourceType;
    sourceId: string;
    quantity?: number;
    couponId?: string | null;
  }, idempotencyKey: string = crypto.randomUUID()): Promise<OrderView> =>
    http.post('/commerce/orders', input, { headers: { 'Idempotency-Key': idempotencyKey } }),
  pay: (orderId: string, channel: PaymentChannel): Promise<CheckoutAction> =>
    http.post(`/commerce/orders/${orderId}/pay`, { channel }),
  closeOrder: (orderId: string): Promise<void> => http.post(`/commerce/orders/${orderId}/close`),
  coupons: (): Promise<UserCouponView[]> => http.get('/commerce/coupons'),
  claimCoupon: (code: string): Promise<UserCouponView> =>
    http.post(
      '/commerce/coupons/claims',
      { code },
      { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    ),
  redeemCoupon: (couponId: string): Promise<UserCouponView> =>
    http.post(
      `/commerce/coupons/${couponId}/redeem`,
      {},
      { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    ),
  creditAccount: (workspaceId?: string): Promise<CreditAccountView> =>
    http.get(
      `/commerce/credits/account${workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
  creditSummary: (
    period: string,
    workspaceId?: string,
    refresh = false,
  ): Promise<CreditUsageSummary> =>
    http.get('/commerce/credits/summary', { params: { period, workspaceId, refresh } }),
  creditMonths: (
    before?: string,
    workspaceId?: string,
  ): Promise<{ items: CreditMonthView[]; nextBefore: string | null }> =>
    http.get('/commerce/credits/months', { params: { before, workspaceId } }),
  creditHistory: (query: CreditLedgerQuery): Promise<CreditLedgerPage> =>
    http.get('/commerce/credits/ledger', { params: query }),
  restoreCredits: (month: string, workspaceId?: string): Promise<{ status: string }> =>
    http.post(
      `/commerce/credits/months/${encodeURIComponent(month)}/restore`,
      {},
      { params: { workspaceId } },
    ),
  creditLedger: (
    page = 1,
    limit = 20,
    workspaceId?: string,
  ): Promise<{ items: CreditLedgerEntryView[]; total: number; page: number; limit: number }> =>
    http.get(
      `/commerce/credits/ledger?page=${page}&limit=${limit}${workspaceId ? `&workspaceId=${encodeURIComponent(workspaceId)}` : ''}`,
    ),
};
